import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import MapView, { relatedPosts, radiusFor } from "@/components/MapView";
import { api } from "@/lib/api";
import { LAYER_TYPES } from "@/lib/mapConfig";

export default function MapPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("site");
  const [markers, setMarkers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(LAYER_TYPES.map((t) => t.id));

  useEffect(() => {
    api.get("/markers").then((r) => setMarkers(r.data)).catch(() => {});
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const toggle = (id) => setActive((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const shown = markers.filter((m) => active.includes(m.type));
  const countByType = (id) => markers.filter((m) => m.type === id).length;

  return (
    <Layout>
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-10">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#059669] mb-3">Interactive Map · New Zealand</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#11181C] tracking-tight">DCEPtNZ tracking map of New Zealand Data Centres</h1>
          <p className="mt-4 text-[#52606D] max-w-2xl leading-relaxed">
            An interactive map of confirmed and proposed data centres, submarine cable landings, the high-voltage grid and renewable generation across New Zealand. Toggle layers and open a site to read the linked investigation.
          </p>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-8 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Controls */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#52606D] mb-4">Layers</div>
            <div className="space-y-1">
              {LAYER_TYPES.map((t) => {
                const on = active.includes(t.id);
                return (
                  <button key={t.id} onClick={() => toggle(t.id)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${on ? "bg-slate-100" : "opacity-50 hover:opacity-80"}`}
                    data-testid={`map-layer-toggle-${t.id}`}>
                    <span className="flex items-center gap-2.5">
                      <span className="w-3 h-3 rounded-full" style={{ background: t.color }} />
                      <span className="text-sm font-medium text-[#11181C]">{t.name}</span>
                    </span>
                    <span className="font-mono text-xs text-[#52606D]">{countByType(t.id)}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-4 bg-[#0B0F17] text-white rounded-xl p-5">
            <div className="font-serif text-3xl">{shown.length}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1">Features shown</div>
          </div>
          <div className="mt-4 bg-white border border-slate-200 rounded-xl p-5" data-testid="map-size-legend">
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#52606D] mb-4">Dot size = capacity</div>
            <div className="flex items-end justify-between gap-2">
              {[10, 40, 100, 300].map((mw) => {
                const r = radiusFor({ capacity: `${mw}MW` });
                return (
                  <div key={mw} className="flex flex-col items-center gap-2">
                    <span className="rounded-full bg-[#11181C]/80 ring-2 ring-white" style={{ width: r * 2, height: r * 2 }} />
                    <span className="font-mono text-[10px] text-[#52606D]">{mw} MW</span>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-[#64748B] mt-4 leading-relaxed">Sites without a published capacity figure are shown at a standard size.</p>
          </div>
        </aside>

        {/* Map */}
        <div>
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
            <MapView markers={shown} posts={posts} height="620px" zoom={6} focusId={focusId} />
          </div>
          <p className="mt-4 text-xs text-[#64748B] leading-relaxed">
            Markers cover publicly announced facilities and infrastructure. Coordinates for some linear features are indicative. Know of a project we should be tracking? Reach out via the Connect page.
          </p>

          {/* Linked sites list */}
          <div className="mt-8 grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden">
            {shown.map((m) => {
              const t = LAYER_TYPES.find((x) => x.id === m.type);
              const linked = relatedPosts(m, posts);
              const focused = m.id === focusId;
              return (
                <div key={m.id} className={`p-5 ${focused ? "bg-emerald-50" : "bg-white"}`} data-testid={`map-site-${m.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em]" style={{ color: t?.color }}>
                      <span className="w-2 h-2 rounded-full" style={{ background: t?.color }} /> {t?.name}
                    </div>
                    <button onClick={() => { setSearchParams({ site: m.id }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#2E8BFF] hover:underline" data-testid={`map-site-locate-${m.id}`}>Locate</button>
                  </div>
                  <div className="font-serif text-lg text-[#11181C] mt-2">{m.name}</div>
                  <div className="text-xs text-[#52606D] mt-1">{[m.operator, m.capacity, m.region].filter(Boolean).join(" · ")}</div>
                  {linked.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {linked.map((p) => (
                        <Link key={p.id} to={`/research/${p.slug}`} className="block text-xs font-semibold text-[#059669] hover:underline" data-testid={`map-site-article-${m.id}-${p.id}`}>→ {p.title}</Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
}
